import React from "react";
import { Menu, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { LOGOS, CATEGORIES } from "../lib/brand";
import { useCart } from "../lib/cart-context";

export default function Navbar() {
  const { totals, setOpen } = useCart();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToCategory = (catId) => {
    scrollTo("shop");
    // small delay to let the section come into view, then dispatch event
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("kf:set-category", { detail: catId }));
    }, 250);
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 w-full border-b border-[#27272A] bg-black/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a
          href="#top"
          data-testid="navbar-logo-link"
          className="flex items-center gap-3"
          onClick={(e) => {
            e.preventDefault();
            scrollTo("top");
          }}
        >
          <img
            src={LOGOS.wordmark}
            alt="Kung Fu TCG"
            className="h-12 w-auto max-w-[200px] object-contain sm:h-14"
          />
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="shop-dropdown-trigger"
                className="text-sm uppercase tracking-wider text-[#F5F5DC] transition-colors hover:text-[#E11D2A]"
              >
                Shop ▾
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              data-testid="shop-dropdown-content"
              className="border-[#27272A] bg-[#121212] text-[#F5F5DC]"
              align="start"
            >
              <DropdownMenuLabel className="font-display tracking-widest text-[#E11D2A]">
                Categories
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#27272A]" />
              {CATEGORIES.map((c) => (
                <DropdownMenuItem
                  key={c.id}
                  data-testid={`shop-dropdown-${c.id}`}
                  className="cursor-pointer focus:bg-[#E11D2A]/15 focus:text-[#F5F5DC]"
                  onClick={() => goToCategory(c.id)}
                >
                  {c.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {[
            ["About", "about"],
            ["Boba", "boba"],
            ["Events", "events"],
            ["Visit", "visit"],
          ].map(([label, id]) => (
            <button
              key={id}
              data-testid={`nav-${id}-link`}
              onClick={() => scrollTo(id)}
              className="text-sm uppercase tracking-wider text-[#F5F5DC] transition-colors hover:text-[#E11D2A]"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            data-testid="open-cart-button"
            variant="ghost"
            size="icon"
            className="relative text-[#F5F5DC] hover:bg-[#E11D2A]/10 hover:text-[#E11D2A]"
            onClick={() => setOpen(true)}
          >
            <ShoppingCart className="h-5 w-5" />
            {totals.count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center bg-[#E11D2A] text-[10px] font-bold text-white"
              >
                {totals.count}
              </span>
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="mobile-menu-button"
                variant="ghost"
                size="icon"
                className="text-[#F5F5DC] md:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="border-[#27272A] bg-[#121212] text-[#F5F5DC]"
            >
              {CATEGORIES.map((c) => (
                <DropdownMenuItem
                  key={c.id}
                  data-testid={`mobile-shop-${c.id}`}
                  onClick={() => goToCategory(c.id)}
                  className="focus:bg-[#E11D2A]/15"
                >
                  Shop {c.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-[#27272A]" />
              {[
                ["About", "about"],
                ["Boba", "boba"],
                ["Events", "events"],
                ["Visit", "visit"],
              ].map(([label, id]) => (
                <DropdownMenuItem
                  key={id}
                  data-testid={`mobile-nav-${id}`}
                  onClick={() => scrollTo(id)}
                  className="focus:bg-[#E11D2A]/15"
                >
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
